import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';

interface ProtectedMassagerRouteProps {
  children: React.ReactNode;
}

/**
 * Protected Route for the Massager (checkin) portal.
 *
 * Family site_type check: the authenticated massager's clinic site_type (or its
 * parent_site_type) must match the URL `:site` segment. Mirrors the Clinic
 * portal ProtectedRoute convention — a massager whose clinic is upsystem can
 * access /medix/massager/* because upsystem is a child of medix.
 */
const ProtectedMassagerRoute: React.FC<ProtectedMassagerRouteProps> = ({ children }) => {
  const auth = useMassagerAuthContext();
  const location = useLocation();
  const { site } = useParams<{ site: string }>();
  const urlSiteType = site || 'medix';

  const { isAuthenticated, isLoading, clinic } = auth;

  const clinicReady = Boolean(clinic?.site_type && clinic?.clinic_number);

  if (isLoading || (isAuthenticated && !clinicReady)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">
            {isLoading ? '読み込み中...' : '施設情報を読み込み中...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/${urlSiteType}/massager/login`}
        state={{ from: location }}
        replace
      />
    );
  }

  // Family check — allow when URL site matches clinic's own site_type or its parent.
  const clinicSiteType = clinic?.site_type;
  const parentSiteType = clinic?.parent_site_type;
  const belongsToFamily =
    clinicSiteType === urlSiteType || parentSiteType === urlSiteType;

  if (clinicSiteType && !belongsToFamily) {
    const redirectTarget = parentSiteType || clinicSiteType;
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4 text-red-500">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-bold text-gray-800">アクセス権限がありません</h1>
          <p className="mb-6 text-gray-600">
            このサイトへのアクセス権限がありません。
            <br />
            ご利用のアカウントは「{redirectTarget}」サイト用です。
          </p>
          <a
            href={`/${redirectTarget}/massager`}
            className="inline-block rounded-md bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
          >
            {redirectTarget}サイトへ移動
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedMassagerRoute;
